import { capturePostLoginRedirect, getValidAccessToken, refreshAccessToken } from "./auth";
import { getConfig } from "./config";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const config = getConfig();
  const token = await getValidAccessToken();

  const url = `${config.apiBaseUrl}${path}`;
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers.authorization = `Bearer ${token}`;
  }

  let response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const newToken = await getValidAccessToken();
      if (newToken) {
        headers.authorization = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      }
    }
  }

  if (response.status === 401) {
    capturePostLoginRedirect(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    window.location.href = "/login";
    throw new ApiError(401, "Unauthorized");
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "Unknown error");
    throw new ApiError(response.status, text);
  }

  return (await response.json()) as T;
}

// ── Conversations ───────────────────────────────────────────

export interface ConversationSummary {
  conversationId: string;
  dealerId: number;
  channel: string;
  state: string;
  lastMessageAt: string;
  updatedAt: string;
  dealerName: string | null;
  dealerOrg: string | null;
}

export async function fetchConversations(): Promise<ConversationSummary[]> {
  const res = await apiFetch<{ conversations: ConversationSummary[] }>("/api/dashboard/conversations");
  return res.conversations;
}

export interface AuditEvent {
  auditId: string;
  timestamp: string;
  dealerId: number | null;
  actor: string;
  authority: string;
  resourceType: string;
  resourceId: string;
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
}

export interface ConversationDetail {
  conversation: ConversationSummary | null;
  messages: ConversationMessage[];
  nextCursor: string | null;
  canSendFreeform: boolean;
  sessionExpired: boolean;
  latestInboundAt: string | null;
  auditEvents: AuditEvent[];
}

export interface ConversationMessage {
  messageId: string;
  conversationId: string;
  dealerId?: string | number;
  direction: string;
  content: string;
  messageType: string;
  timestamp: string;
  metadata: Record<string, unknown>;
}

export interface SignedMediaInfo {
  signedUrl: string;
  /** e.g. "image/jpeg" or "audio/ogg" - not every WhatsApp attachment is a photo. */
  contentType: string;
}

export async function fetchMessageMediaSignedUrl(messageId: string, index: number): Promise<SignedMediaInfo> {
  return apiFetch<SignedMediaInfo>(`/api/dashboard/messages/${encodeURIComponent(messageId)}/media/${index}/signed-url`);
}

export async function fetchConversation(
  dealerId: string,
  params?: { cursor?: string; limit?: number },
): Promise<ConversationDetail> {
  const query = new URLSearchParams();
  if (params?.cursor) query.set("cursor", params.cursor);
  if (params?.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiFetch<ConversationDetail>(`/api/dashboard/conversation/${dealerId}${qs ? `?${qs}` : ""}`);
}

export function postConversationReply(body: {
  dealerId: string;
  content: string;
  senderName?: string;
}): Promise<{ success: boolean; reason?: string; message?: string }> {
  return apiFetch("/api/dashboard/conversation-reply", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ── Support Queue ───────────────────────────────────────────

export interface SupportTicketMessage {
  messageId: string;
  direction: "inbound" | "outbound";
  content: string;
  timestamp: string;
  messageType: string;
  sentBy: string | null;
}

export interface SupportTicket {
  ticketId: string;
  conversationId: string;
  dealerId: number;
  dealerFirstName: string;
  dealerLastName: string;
  dealerOrgName: string;
  dealerDealership: string;
  status: string;
  priority: string;
  assignedTeam: string | null;
  assignedTo: string | null;
  suggestedResponses: string[];
  suggestedActions: string[];
  escalationReason: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  recentMessages: SupportTicketMessage[];
}

export interface SupportQueuePagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface SupportQueueResult {
  tickets: SupportTicket[];
  pagination: SupportQueuePagination;
}

export interface FetchSupportQueueParams {
  status?: string;
  team?: string;
  limit?: number;
  offset?: number;
}

export async function fetchSupportQueue(params?: FetchSupportQueueParams): Promise<SupportQueueResult> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set("status", params.status);
  if (params?.team) qs.set("team", params.team);
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  const query = qs.toString();
  const res = await apiFetch<{ tickets: SupportTicket[]; pagination: SupportQueuePagination }>(
    `/api/dashboard/support-queue${query ? `?${query}` : ""}`,
  );
  return { tickets: res.tickets, pagination: res.pagination };
}

export function postSupportResponse(body: {
  ticketId: string;
  content: string;
  source: string;
  senderName?: string;
}): Promise<{ success: boolean }> {
  const { ticketId, ...rest } = body;
  return apiFetch(`/api/dashboard/support-tickets/${encodeURIComponent(ticketId)}/response`, {
    method: "POST",
    body: JSON.stringify(rest),
  });
}

export function postAssignTask(body: {
  ticketId: string;
  team: string;
  assignedTo: string;
}): Promise<{ success: boolean }> {
  const { ticketId, ...rest } = body;
  return apiFetch(`/api/dashboard/support-tickets/${encodeURIComponent(ticketId)}/assign`, {
    method: "POST",
    body: JSON.stringify(rest),
  });
}

export function postResolveTicket(ticketId: string): Promise<{ success: boolean }> {
  return apiFetch(`/api/dashboard/support-tickets/${encodeURIComponent(ticketId)}/resolve`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

// ── Identity Conflicts ──────────────────────────────────────

export interface MatchedUserSummary {
  userId: number;
  firstName: string;
  lastName: string;
  dealership: string;
  orgName: string;
  email: string;
  lastBidAt: string | null;
}

export interface IdentityConflict {
  conflictId: string;
  phoneNumber: string;
  matchedUserIds: number[];
  matchedUsers: MatchedUserSummary[];
  status: string;
  createdAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolution: string | null;
  resolvedUserId: number | null;
}

export async function fetchIdentityConflicts(): Promise<IdentityConflict[]> {
  const res = await apiFetch<{ conflicts: IdentityConflict[] }>("/api/dashboard/identity-conflicts");
  return res.conflicts;
}

export function postResolveConflict(body: {
  phoneNumber: string;
  resolution: string;
  resolvedUserId: number;
}): Promise<{ success: boolean }> {
  return apiFetch("/api/dashboard/resolve-conflict", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ── Review Queue ────────────────────────────────────────────

export interface ReviewFlag {
  flagId: string;
  reviewType: string;
  severity: string;
  dealerId: number | null;
  conversationId: string | null;
  description: string;
  evidence: Record<string, unknown>;
  createdAt: string;
  reviewedAt: string | null;
}

export async function fetchReviewQueue(): Promise<ReviewFlag[]> {
  const res = await apiFetch<{ flags: ReviewFlag[] }>("/api/dashboard/review-queue");
  return res.flags;
}

// ── Dealer Profile ──────────────────────────────────────────

export interface RecommendationFeedbackEntry {
  feedbackId: string;
  dealerId: number;
  projectId: number;
  sentiment: "not_for_me";
  templateName: string;
  reason: string | null;
  vehicleContext: {
    projectId: number;
    vehicleTitle: string | null;
    year: number | null;
    brand: string | null;
    model: string | null;
    fuelType: string | null;
    transmission: string | null;
    mileage: number | null;
    mileageUnit: string | null;
    bodyType: string | null;
    estimatedPrice: number | null;
  };
  conversationId: string;
  originalMessageSid: string | null;
  createdAt: string;
  reasonReceivedAt: string | null;
  analysedAt: string | null;
}

export interface StockInsightEntry {
  insightId: string;
  category:
    | "price"
    | "mileage"
    | "year"
    | "make"
    | "model"
    | "body_type"
    | "fuel_type"
    | "transmission"
    | "condition"
    | "location"
    | "other";
  text: string;
  examples: number[];
  firstSeenAt: string;
  lastSeenAt: string;
  occurrenceCount: number;
}

export interface GeneralSignalEntry {
  signalId: string;
  category: "overstocked" | "out_of_office" | "budget_constrained" | "industry_event" | "other";
  text: string;
  firstSeenAt: string;
  lastSeenAt: string;
  occurrenceCount: number;
}

export interface DealerStockInsights {
  dealerId: number;
  insights: StockInsightEntry[];
  generalSignals: GeneralSignalEntry[];
  version: number;
  updatedAt: string;
}

export interface DealerProfile {
  dealer: {
    dealerId: number;
    email: string;
    displayName: string;
    orgName: string;
    phone: string;
    channel: string;
    status: string;
  };
  preferences: {
    notifications: Record<string, unknown>;
    vehicles: Record<string, unknown>;
    version: number;
  };
  botPreferences: {
    ottoBot: boolean;
    outbidBot: boolean;
    stockNotifications: boolean;
  };
  /** Internal-team-only capabilities (never dealer-controlled). */
  internalCapabilities: {
    tb360Bidding: boolean;
    personalisedMessagesBlocked: boolean;
  };
  /** Enforced seller_config.stock_filters; an absent range/list field = no restriction. */
  stockFilters: StockFilters;
  /** Every valid stock type with its label, served by the backend (its list is the source of truth). */
  stockTypeOptions: StockTypeOption[];
  feedback: RecommendationFeedbackEntry[];
  insights: DealerStockInsights | null;
}

export function fetchDealerProfile(dealerId: string): Promise<DealerProfile> {
  return apiFetch(`/api/dashboard/dealer-profile/${dealerId}`);
}

// ── Dealer bot preferences ──────────────────────────────────

export type BotPreferenceKey = "otto_bot" | "outbid_bot" | "stock_notifications";

export interface BotPreferences {
  dealerId: number;
  ottoBot: boolean;
  outbidBot: boolean;
  stockNotifications: boolean;
}

export interface BotPreferenceUpdateResult {
  key: BotPreferenceKey;
  value: boolean;
  welcomeSent: boolean;
  cascaded: Partial<Record<"outbid_bot" | "stock_notifications", boolean>>;
}

export function fetchBotPreferences(dealerId: string): Promise<BotPreferences> {
  return apiFetch(`/api/dashboard/dealers/${encodeURIComponent(dealerId)}/bot-preferences`);
}

export function postBotPreference(
  dealerId: string,
  key: BotPreferenceKey,
  value: boolean,
): Promise<BotPreferenceUpdateResult> {
  return apiFetch(`/api/dashboard/dealers/${encodeURIComponent(dealerId)}/bot-preferences`, {
    method: "POST",
    body: JSON.stringify({ key, value }),
  });
}

// ── Internal-team-only dealer capabilities ──────────────────

export interface Tb360BiddingUpdateResult {
  key: "tb360_bidding";
  value: boolean;
}

export function postTb360Bidding(dealerId: string, enabled: boolean): Promise<Tb360BiddingUpdateResult> {
  return apiFetch(`/api/dashboard/dealers/${encodeURIComponent(dealerId)}/tb360-bidding`, {
    method: "POST",
    body: JSON.stringify({ enabled }),
  });
}

export interface PersonalisedMessagesBlockedUpdateResult {
  key: "personalised_messages_blocked";
  value: boolean;
}

export function postPersonalisedMessagesBlocked(
  dealerId: string,
  blocked: boolean,
): Promise<PersonalisedMessagesBlockedUpdateResult> {
  return apiFetch(`/api/dashboard/dealers/${encodeURIComponent(dealerId)}/personalised-messages-blocked`, {
    method: "POST",
    body: JSON.stringify({ blocked }),
  });
}

// ── Dealer stock filters ────────────────────────────────────

export interface StockFilterRange {
  min?: number;
  max?: number;
}

export interface StockTypeOption {
  value: string;
  label: string;
}

/**
 * makes/bodyTypes are allow-lists: when set, only matching listings are sent.
 * stock_type is the enabled stock types; an empty list means NO stock alerts.
 */
export interface StockFilters {
  stock_type: string[];
  age?: StockFilterRange;
  mileage?: StockFilterRange;
  price?: StockFilterRange;
  makes?: string[];
  bodyTypes?: string[];
}

/** Each field present is written; null clears a range/list. stock_type is replaced wholesale ([] = none). */
export type StockFiltersUpdate = {
  [K in Exclude<keyof StockFilters, "stock_type">]?: StockFilters[K] | null;
} & { stock_type?: string[] };

export interface StockFiltersUpdateResult {
  dealerId: number;
  stockFilters: StockFilters;
}

export function postStockFilters(dealerId: string, update: StockFiltersUpdate): Promise<StockFiltersUpdateResult> {
  return apiFetch(`/api/dashboard/dealers/${encodeURIComponent(dealerId)}/stock-filters`, {
    method: "POST",
    body: JSON.stringify(update),
  });
}

// ── Stock alert caps ────────────────────────────────────────

export type StockType = "trade" | "tradedesk_onsite" | "tradedesk_offsite" | "oem";

export const STOCK_TYPES: StockType[] = ["trade", "tradedesk_onsite", "tradedesk_offsite", "oem"];

export const STOCK_TYPE_LABELS: Record<StockType, string> = {
  trade: "Trade",
  tradedesk_onsite: "TradeDesk Onsite",
  tradedesk_offsite: "TradeDesk Offsite",
  oem: "OEM",
};

/** null = unlimited (no daily cap). */
export type StockAlertCaps = Record<StockType, number | null>;

export interface DealerStockAlertCaps {
  dealerId: number;
  overrides: Partial<StockAlertCaps>;
  platformDefaults: StockAlertCaps;
  effective: StockAlertCaps;
}

export function fetchDealerStockAlertCaps(dealerId: string): Promise<DealerStockAlertCaps> {
  return apiFetch(`/api/dashboard/dealers/${encodeURIComponent(dealerId)}/stock-alert-caps`);
}

export function postDealerStockAlertCapOverride(
  dealerId: string,
  stockType: StockType,
  value: number | null | "inherit",
): Promise<DealerStockAlertCaps> {
  return apiFetch(`/api/dashboard/dealers/${encodeURIComponent(dealerId)}/stock-alert-caps`, {
    method: "POST",
    body: JSON.stringify({ stockType, value }),
  });
}

export async function fetchPlatformStockAlertCaps(): Promise<StockAlertCaps> {
  const res = await apiFetch<{ caps: StockAlertCaps }>("/api/dashboard/platform-settings/stock-alert-caps");
  return res.caps;
}

export async function postPlatformStockAlertCaps(caps: Partial<StockAlertCaps>): Promise<StockAlertCaps> {
  const res = await apiFetch<{ caps: StockAlertCaps }>("/api/dashboard/platform-settings/stock-alert-caps", {
    method: "POST",
    body: JSON.stringify({ caps }),
  });
  return res.caps;
}

export async function fetchPlatformSupportNotificationEmails(): Promise<string[]> {
  const res = await apiFetch<{ emails: string[] }>("/api/dashboard/platform-settings/support-notification-emails");
  return res.emails;
}

export async function postPlatformSupportNotificationEmails(emails: string[]): Promise<string[]> {
  const res = await apiFetch<{ emails: string[] }>("/api/dashboard/platform-settings/support-notification-emails", {
    method: "POST",
    body: JSON.stringify({ emails }),
  });
  return res.emails;
}

export interface EngagementAutoDisableThresholds {
  zeroDeliveryDays: number;
  zeroReadDays: number;
}

export async function fetchEngagementAutoDisableThresholds(): Promise<EngagementAutoDisableThresholds> {
  const res = await apiFetch<{ thresholds: EngagementAutoDisableThresholds }>(
    "/api/dashboard/platform-settings/engagement-auto-disable-thresholds",
  );
  return res.thresholds;
}

export async function postEngagementAutoDisableThresholds(
  thresholds: Partial<EngagementAutoDisableThresholds>,
): Promise<EngagementAutoDisableThresholds> {
  const res = await apiFetch<{ thresholds: EngagementAutoDisableThresholds }>(
    "/api/dashboard/platform-settings/engagement-auto-disable-thresholds",
    {
      method: "POST",
      body: JSON.stringify({ thresholds }),
    },
  );
  return res.thresholds;
}

export function migrateDealer(dealerId: number): Promise<{ welcomeSent: boolean }> {
  return apiFetch(`/api/dashboard/dealers/${encodeURIComponent(String(dealerId))}/migrate-legacy`, {
    method: "POST",
  });
}

// ── Dealers list ────────────────────────────────────────────

export interface DealerListItem {
  dealerId: number;
  firstName: string;
  lastName: string;
  orgName: string;
  dealership: string;
  email: string;
  phone: string;
  orgCountry: string;
  status: string;
  lastOttoInboundAt: string | null;
  isLegacy: boolean;
}

export interface DealerListResponse {
  dealers: DealerListItem[];
  total: number;
  hasMore: boolean;
}

export interface FetchDealersParams {
  search?: string;
  onlyOttoEnabled?: boolean;
  showLegacy?: boolean;
  limit?: number;
  offset?: number;
}

export function fetchDealers(params: FetchDealersParams = {}): Promise<DealerListResponse> {
  const query = new URLSearchParams();
  if (params.search) query.set("search", params.search);
  if (params.onlyOttoEnabled === false) query.set("onlyOttoEnabled", "false");
  if (params.showLegacy === true) query.set("showLegacy", "true");
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  if (params.offset !== undefined) query.set("offset", String(params.offset));
  const qs = query.toString();
  return apiFetch(`/api/dashboard/dealers${qs ? `?${qs}` : ""}`);
}

// ── Observability ───────────────────────────────────────────

export interface ObservabilityEvent {
  eventId: string;
  eventType: string;
  dealerId: number | null;
  conversationId: string | null;
  timestamp: string;
  data: Record<string, unknown>;
  tokenUsage: { inputTokens: number; outputTokens: number; model: string; estimatedCostUsd: number } | null;
  outcome: string;
  outcomeReason: string | null;
}

export interface PaginatedEvents {
  events: ObservabilityEvent[];
  nextCursor: string | null;
}

export async function fetchObservabilityEvents(params?: {
  eventType?: string;
  dealerId?: string;
  cursor?: string;
  limit?: number;
}): Promise<PaginatedEvents> {
  const query = new URLSearchParams();
  if (params?.eventType) query.set("eventType", params.eventType);
  if (params?.dealerId) query.set("dealerId", params.dealerId);
  if (params?.cursor) query.set("cursor", params.cursor);
  if (params?.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiFetch<PaginatedEvents>(`/api/dashboard/observability${qs ? `?${qs}` : ""}`);
}

// ── Outbid Engagement ───────────────────────────────────────

export type OutbidEngagementOutcome = "success" | "failed" | "pending";

export interface OutbidEngagementEntry {
  id: string;
  dealerId: string;
  dealerName: string | null;
  projectId: number;
  vehicleTitle: string | null;
  vehicleVin: string | null;
  outbidAt: string;
  outbidOnAmount: number | null;
  newBidAmount: number | null;
  currency: string | null;
  respondedAt: string;
  outcome: OutbidEngagementOutcome;
  unsuccessfulReason?: string | null;
  errorMessage?: string | null;
}

export interface PaginatedOutbidEngagements {
  items: OutbidEngagementEntry[];
  total: number;
  nextCursor: string | null;
}

export async function fetchOutbidEngagements(params?: {
  dealerId?: string;
  cursor?: string;
  limit?: number;
}): Promise<PaginatedOutbidEngagements> {
  const query = new URLSearchParams();
  if (params?.dealerId) query.set("dealerId", params.dealerId);
  if (params?.cursor) query.set("cursor", params.cursor);
  if (params?.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiFetch<PaginatedOutbidEngagements>(`/api/dashboard/outbid-engagements${qs ? `?${qs}` : ""}`);
}

// ── TB360 Bid Engagement ────────────────────────────────────

export type Tb360BidEngagementOutcome = OutbidEngagementOutcome;

export interface Tb360BidEngagementEntry {
  id: string;
  dealerId: string;
  dealerName: string | null;
  projectId: number;
  vehicleTitle: string | null;
  vehicleVin: string | null;
  tappedAt: string;
  bidAmount: number | null;
  currency: string | null;
  respondedAt: string | null;
  outcome: Tb360BidEngagementOutcome;
  unsuccessfulReason?: string | null;
  errorMessage?: string | null;
}

export interface PaginatedTb360BidEngagements {
  items: Tb360BidEngagementEntry[];
  total: number;
  nextCursor: string | null;
}

export async function fetchTb360BidEngagements(params?: {
  dealerId?: string;
  cursor?: string;
  limit?: number;
}): Promise<PaginatedTb360BidEngagements> {
  const query = new URLSearchParams();
  if (params?.dealerId) query.set("dealerId", params.dealerId);
  if (params?.cursor) query.set("cursor", params.cursor);
  if (params?.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiFetch<PaginatedTb360BidEngagements>(`/api/dashboard/tb360-bid-engagements${qs ? `?${qs}` : ""}`);
}

// ── Manual Recommendation ───────────────────────────────────

export function postManualRecommendation(body: {
  userId: number;
  vehicleId: number;
  senderName: string;
  senderPhotoUrl: string | null;
  customMessage: string;
}): Promise<{ success: boolean }> {
  return apiFetch("/api/dashboard/manual-recommend", {
    method: "POST",
    body: JSON.stringify({ ...body, bypassLimits: true }),
  });
}
